<script lang="ts" setup>
const { loadingPortal, openPortal } = useSubscription()
const { t } = useI18n()
const { closed } = useClosable('subscriptionStatusAlert')
const billingStore = useBillingStore()
const localePath = useLocalePath()
const teamsStore = useTeamsStore()
const userStore = useUserStore()

const buttons = computed((): UButton[] => {
  const buttons: UButton[] = []

  const unpaidStatuses = [
    'incomplete',
    'past_due',
    'unpaid',
  ]

  if (unpaidStatuses.includes(billingStore.subscription?.status || '')) {
    buttons.push({
      icon: getIcon('document'),
      id: 'payInvoice',
      label: t('globals.payInvoice'),
      loading: loadingPortal.value,
      size: 'sm',
    })
  }

  if ((teamsStore?.currentTeam?.creditsPack || 0) > 500) {
    buttons.push({
      icon: getIcon('pricing'),
      id: 'buyCredits',
      label: t('buyCredits'),
      size: 'sm',
      to: localePath('settings-subscription'),
      ui: { icon: { base: 'text-green-400 dark:text-green-600' } },
    })
  }
  else {
    buttons.push({
      icon: getIcon('arrowPointingTop'),
      id: 'chooseSubscription',
      label: t('chooseSubscription'),
      size: 'sm',
      to: localePath('settings-subscription'),
      ui: { icon: { base: 'text-blue-400 dark:text-blue-600' } },
    })
  }

  if (hasNoCredits.value && (teamsStore?.currentTeam?.creditsPack || 0) === 500) {
    buttons.push({
      icon: getIcon('pricing'),
      id: 'discoverOurPricing',
      label: t('discoverOurPricing'),
      size: 'sm',
      to: localePath('settings-subscription'),
      ui: { icon: { base: 'text-yellow-400 dark:text-yellow-600' } },
    })
  }

  if (hasNoCredits.value && (teamsStore?.currentTeam?.creditsPack || 0) > 500) {
    buttons.push({
      icon: getIcon('arrowPointingTop'),
      id: 'upgradePlan',
      label: t('upgradePlan'),
      size: 'sm',
      to: localePath('settings-subscription'),
      ui: { icon: { base: 'text-blue-400 dark:text-blue-600' } },
    })
  }

  return buttons
})

const hasNoCredits = computed(() => {
  return teamsStore.currentTeam && teamsStore.currentTeam.credits <= 0 && teamsStore.currentTeam.creditsPack !== 0
})

const title = computed(() => {
  if (['canceled'].includes(billingStore.subscription?.status || '')) {
    return t('title.subscription.canceled')
  }

  if (['incomplete'].includes(billingStore.subscription?.status || '')) {
    return t('title.subscription.incomplete')
  }

  if (['past_due', 'unpaid'].includes(billingStore.subscription?.status || '')) {
    return t('title.subscription.unpaid')
  }

  if (hasNoCredits.value && (teamsStore?.currentTeam?.creditsPack || 0) > 500) {
    return t('noCreditsRemaining')
  }

  if (hasNoCredits.value && (teamsStore?.currentTeam?.creditsPack || 0) <= 500) {
    return t('noCreditsRemainingVisitor')
  }

  return ''
})

async function onButtonClick(id?: string) {
  if (id === 'payInvoice') {
    try {
      await openPortal()
    }
    catch (error) {
      console.error('Failed to open portal:', error)
    }
  }
}
</script>

<template>
  <div
    v-if="!closed && userStore.user && (hasNoCredits || (billingStore.subscription && !billingStore.isSubscriptionActive))"
    class="fixed bottom-10 left-1/2 z-20 flex w-[320px] -translate-x-1/2 transform flex-col gap-4 rounded-lg bg-gray-100 p-4 shadow ring-1 ring-gray-200 md:w-[520px] lg:w-[620px] dark:bg-gray-900 dark:ring-gray-800"
  >
    <div class="flex flex-col items-center gap-1 md:flex-row md:gap-3">
      <BasePowerHead
        power="magnifier"
        size="2xs"
      />

      {{ title }}
    </div>

    <div class="flex gap-2">
      <UButton
        v-for="button in buttons"
        :key="button.id"
        :color="button.color"
        :icon="button.icon"
        :label="button.label"
        :loading="button.loading"
        :size="button.size"
        :to="button.to"
        :ui="button.ui"
        :variant="button.variant"
        @click="onButtonClick(button.id)"
      />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "buyCredits": "Buy more credits",
    "chooseSubscription": "Choose a plan",
    "discoverOurPricing": "Discover our pricing",
    "noCreditsRemaining": "You do not have any credits remaining.",
    "noCreditsRemainingVisitor": "You do not have any free credits remaining.",
    "title": {
      "subscription": {
        "canceled": "We canceled your subscription as we cannot process the payment.",
        "incomplete": "Your subscription is incomplete as we could not process the payment.",
        "unpaid": "We had an issue when processing the payment of your subscription."
      }
    },
    "upgradePlan": "Upgrade your plan"
  },
  "es": {
    "buyCredits": "Comprar más créditos",
    "chooseSubscription": "Elige un plan",
    "discoverOurPricing": "Descubre nuestros precios",
    "noCreditsRemaining": "No tienes créditos restantes.",
    "noCreditsRemainingVisitor": "No tienes créditos gratuitos restantes.",
    "title": {
      "subscription": {
        "canceled": "Cancelamos tu suscripción porque no pudimos procesar el pago.",
        "incomplete": "Tu suscripción está incompleta porque no pudimos procesar el pago.",
        "unpaid": "Hubo un problema al procesar el pago de tu suscripción."
      }
    },
    "upgradePlan": "Actualiza tu plan"
  },
  "fr": {
    "buyCredits": "Acheter plus de crédits",
    "chooseSubscription": "Choisir un plan",
    "discoverOurPricing": "Découvrez nos tarifs",
    "noCreditsRemaining": "Vous n'avez plus de crédits restants.",
    "noCreditsRemainingVisitor": "Vous n'avez plus de crédits gratuits restants.",
    "title": {
      "subscription": {
        "canceled": "Nous avons annulé votre abonnement car nous ne pouvons pas traiter le paiement.",
        "incomplete": "Votre abonnement est incomplet car nous n'avons pas pu traiter le paiement.",
        "unpaid": "Nous avons rencontré un problème lors du paiement de votre abonnement."
      }
    },
    "upgradePlan": "Mettre à niveau votre plan"
  }
}
</i18n>
